<template>
    <div
        :class="{ 'ref-user-button': true, 'ref-user-button--mini': mini }"
        :style="{ backgroundColor: bgColor }"
        @click="onClick"
    >
        <span v-if="!mini" class="ref-user-button__text">{{ text }}</span>

        <div class="ref-user-button__icon-container" v-if="!mini" @click.stop="copyLink">
            <img
                v-if="!hasError && !isLoading"
                class="ref-user-button__btn-icon"
                src="~@/assets/images/ref-user-link/ref-copy-icon.svg"
                alt="Реферальная ссылка"
            />

            <img
                v-if="hasError && !isLoading"
                class="ref-user-button__btn-icon"
                src="~@/assets/images/ref-user-link/ref-reload-icon.svg"
                alt="Попробовать еще раз"
            />

            <img
                v-if="isLoading"
                class="ref-user-button__btn-icon ref-user-button__btn-icon--loading"
                src="~@/assets/images/ref-user-link/ref-loading-icon.svg"
                alt="Загрузка..."
            />

            <div class="ref-user-button__tooltip-wrapper">
                <div v-if="hasError" class="ref-user-button__tooltip ref-user-button__tooltip--error">
                    {{ tooltipTextError }}
                </div>
                <div v-if="isCopied" class="ref-user-button__tooltip ref-user-button__tooltip--copied">
                    {{ tooltipTextCopied }}
                </div>
            </div>
        </div>

        <div class="ref-user-button__icon-container-mini" v-if="mini">
            <div class="ref-user-button__icon-container" @click.stop="copyLink">
                <img
                    v-if="!hasError && !isLoading"
                    class="ref-user-button__btn-icon"
                    src="~@/assets/images/ref-user-link/ref-copy-icon-white.svg"
                    alt="Реферальная ссылка"
                />
                <img
                    v-if="hasError && !isLoading"
                    class="ref-user-button__btn-icon"
                    src="~@/assets/images/ref-user-link/ref-reload-icon-white.svg"
                    alt="Попробовать еще раз"
                />

                <img
                    v-if="isLoading"
                    class="ref-user-button__btn-icon ref-user-button__btn-icon--loading"
                    src="~@/assets/images/ref-user-link/ref-loading-icon-white.svg"
                    alt="Загрузка..."
                />
            </div>
            <span v-if="hasError" class="ref-user-button__text--error">{{ tooltipTextError }}</span>
            <span v-if="isCopied" class="ref-user-button__text">{{ tooltipTextCopied }}</span>
            <span v-if="!isCopied && !hasError" class="ref-user-button__text">{{ text }}</span>
        </div>
    </div>
</template>

<script>
import { get } from '@/tools/http';

export default {
    name: 'RefUserButton',

    props: {
        isFullEvent: {
            type: Boolean,
            default: false,
        },
        text: {
            type: String,
            default: 'Пригласи друга',
        },

        refLink: {
            type: String,
            default: '',
        },

        onlyCopy: {
            type: Boolean,
            default: false,
        },

        bgColor: {
            type: String,
            default: '#f6fafd',
        },

        mini: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        hasError: false,
        isLoading: false,
        isCopied: false,
    }),

    computed: {
        tooltipTextError() {
            return 'Попробуй еще раз';
        },

        tooltipTextCopied() {
            return 'Ссылка скопирована';
        },

        rsvDomain() {
            return window.rsvDomain || 'rsv.ru';
        },
    },

    methods: {
        async onClick() {
            if (!this.isFullEvent) {
                const [refLink, friendsCount, scoresCount] = await Promise.all([
                    this.generateRefLink(),
                    this.fetchFriendsCount(),
                    this.fetchScoresCount(),
                ]);

                if (refLink) {
                    this.$emit('onRefLink', refLink);
                    this.$emit('onFriendsCount', friendsCount);
                    this.$emit('onScoresCount', scoresCount);

                    this.$emit('click');
                }
            } else {
                this.copyToClipboard(this.refLink);
            }
        },

        async copyLink() {
            if (this.onlyCopy) {
                this.copyToClipboard(this.refLink);
                return;
            }

            this.isLoading = true;

            try {
                const refLink = await this.generateRefLink();

                if (refLink) {
                    this.$emit('onRefLink', refLink);

                    // Обход ограничения user gesture на мобилках. Способо не очень надежный, но работает.
                    // Приходится обходить ограничение, так как нет возможности сделать клик на кнопку
                    // и сразу копировать ссылку, логика требует сначала обратится к api за получением refLink,
                    // что обрывает user gesture на мобилках.
                    setTimeout(() => {
                        this.copyToClipboard(refLink);
                    }, 0);
                } else {
                    this.copyError();
                }
            } catch (err) {
                this.copyError();
                console.error(err);
            } finally {
                setTimeout(() => (this.isLoading = false), 500);
            }
        },

        copyError() {
            this.hasError = true;
            this.isCopied = false;
        },

        copyToClipboard(link) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(link)
                    .then(() => {
                        this.isCopied = true;
                        setTimeout(() => (this.isCopied = false), 3000);
                    })
                    .catch((err) => {
                        console.warn('Clipboard writeText failed:', err);
                    });
            }
        },

        async generateRefLink() {
            try {
                const response = await get('referral', 'api/Referer', {}, true);

                if (response.refererId) {
                    return `https://${this.rsvDomain}/referral/${response.refererId}`;
                }

                return null;
            } catch (error) {
                this.copyError();
                console.error(error);
                return null;
            }
        },

        async fetchFriendsCount() {
            try {
                const response = await get('referral', 'api/Referer/referrals', {}, true);

                return response.referralsCount || 0;
            } catch (error) {
                console.error(error);
                return null;
            }
        },

        async fetchScoresCount() {
            try {
                const response = await get('coins', 'api/Referral/scores', {}, true);

                return response.scoresCount;
            } catch (error) {
                console.error(error);
                return null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';

.ref-user-button {
    background-color: #f6fafd;
    border-radius: urem(10);
    margin: urem(10) 0;
    padding: 0 urem(10);
    height: urem(44);
    min-width: urem(184);
    max-width: urem(219);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &--mini {
        width: auto;
        height: auto;
        min-width: auto;
        max-width: auto;
        padding: 0;
        margin: 0;

        .ref-user-button__icon-container {
            width: urem(60);
            background: rgba(169, 157, 240, 1);
            border-radius: 50%;
            margin: 0;
            padding: 0;
            width: urem(32);
            height: urem(32);

            @include on-desktop {
                width: urem(40);
                height: urem(40);
            }
        }

        .ref-user-button__btn-icon {
            width: urem(16);
            height: urem(16);

            @include on-desktop {
                width: urem(20);
                height: urem(20);
            }
        }
    }

    &__icon-container-mini {
        width: urem(70);
        display: flex;
        align-items: center;
        flex-direction: column;

        .ref-user-button__text {
            font-weight: 500;
            font-size: urem(10);
            margin-top: urem(4);
            line-height: urem(14);

            @include on-desktop {
                margin-top: urem(13);
            }
        }
    }

    &__text {
        font-weight: 500;
        font-size: urem(14);

        &--error {
            color: rgba(219, 108, 97, 1);
        }
    }

    &__icon-container {
        background: linear-gradient(166.06deg, #e8f9f1 -0.01%, #e9faeb 19.25%, #e8ffe8 50.48%, #ccf8f8 98.8%);
        position: relative;
        width: urem(36);
        height: urem(36);
        border-radius: urem(10);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: urem(5);
    }

    &__btn-icon {
        width: urem(24);
        height: urem(24);

        &--loading {
            animation: spin 2s linear infinite;
        }
    }

    &__tooltip-wrapper {
        position: absolute;
        top: urem(40);
        right: urem(-10);
        pointer-events: none;
    }

    &__tooltip {
        background-color: #fff;
        border-radius: urem(10);
        display: flex;
        align-items: center;
        white-space: nowrap;
        height: urem(32);
        padding: 0 urem(10);
        box-shadow: 0px 2px 10px 0px rgba(70, 99, 171, 0.15);
        font-size: urem(10);
        font-weight: 500;

        &--error {
            color: rgba(219, 108, 97, 1);
        }
    }
}
</style>
